import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useCallback, useEffect, useRef } from 'react';
import { isNativeAndroid, isNativeIOS, requireAppUpdate } from './NativeUtil';
import { setPageview } from './ga';

const VERSION_CHECK_TTL_MS = 120 * 1000;
const VERSION_ENDPOINT = '/version.json';
const SESSION_APPLIED_VERSION_KEY = 'appliedWebVersion';
const RELOAD_ALLOWED_ROUTE_PREFIXES = [
  '/home',
];

function isReloadAllowedRoute(pathname) {
  return RELOAD_ALLOWED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

export default function OnRouterChanged() {
  const location = useLocation();
  const inFlightRef = useRef(false);
  const lastCheckedAtRef = useRef(0);
  const pendingVersionRef = useRef(null);
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // 페이지 변경 시 GA 페이지뷰 전송
  useEffect(() => {
    setPageview(location.pathname + location.search);
  }, [location]);

  const shouldForceUpdate =
    location.pathname !== '/ask_update' &&
    (isNativeIOS() || isNativeAndroid()) &&
    requireAppUpdate();

  const applyPendingVersionIfSafe = useCallback(() => {
    const pendingVersion = pendingVersionRef.current;
    if (!pendingVersion) {
      return false;
    }

    const appliedVersion = window.sessionStorage.getItem(SESSION_APPLIED_VERSION_KEY);
    if (appliedVersion === pendingVersion) {
      return false;
    }

    window.sessionStorage.setItem(SESSION_APPLIED_VERSION_KEY, pendingVersion);
    window.location.reload();
    return true;
  }, []);

  const checkVersion = useCallback(
    async () => {
      if (shouldForceUpdate || inFlightRef.current) {
        return;
      }

      if (!isReloadAllowedRoute(pathnameRef.current)) {
        return;
      }

      if (applyPendingVersionIfSafe()) {
        return;
      }

      const now = Date.now();
      if (now - lastCheckedAtRef.current < VERSION_CHECK_TTL_MS) {
        return;
      }

      lastCheckedAtRef.current = now;
      inFlightRef.current = true;
      try {
        const response = await fetch(VERSION_ENDPOINT, { cache: 'no-store' });
        if (!response.ok) {
          return;
        }

        const payload = await response.json();
        const latestVersion = payload?.version?.trim?.();
        if (!latestVersion) {
          return;
        }

        const storedVersion = window.sessionStorage.getItem(SESSION_APPLIED_VERSION_KEY);

        if (!storedVersion) {
          window.sessionStorage.setItem(SESSION_APPLIED_VERSION_KEY, latestVersion);
          return;
        }

        if (latestVersion === storedVersion) {
          return;
        }

        pendingVersionRef.current = latestVersion;
        applyPendingVersionIfSafe();
      } catch (_error) {
      } finally {
        inFlightRef.current = false;
      }
    },
    [applyPendingVersionIfSafe, shouldForceUpdate]
  );

  useEffect(() => {
    checkVersion();
  }, [checkVersion, location.pathname]);

  if (shouldForceUpdate) {
    return <Navigate to="/ask_update" replace />;
  }

  return <Outlet />;
}
